<script setup lang="ts">
import { ListChecks, ListFilter, RefreshCw } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { i18n } from '@/plugins/internationalization/i18n';
import type { FacetOption } from './types';

interface Props {
    title: string;
    options: FacetOption[];
    modelValue?: (string | number)[];
    align?: 'start' | 'end';
    /** Offers Select all beside Clear; it ticks every option the search currently shows. */
    selectAll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    align: 'start',
    selectAll: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: (string | number)[]];
}>();

const search = ref('');

const visibleOptions = computed(() => {
    const term = search.value.trim().toLowerCase();

    if (!term) {
        return props.options;
    }

    return props.options.filter((option) => option.label.toLowerCase().includes(term));
});

const selectedLabels = computed(() => props.options.filter((option) => props.modelValue.includes(option.value)).map((option) => option.label));

function isSelected(value: string | number): boolean {
    return props.modelValue.includes(value);
}

function toggle(value: string | number) {
    emit('update:modelValue', isSelected(value) ? props.modelValue.filter((v) => v !== value) : [...props.modelValue, value]);
}

const allVisibleSelected = computed(() => visibleOptions.value.every((option) => isSelected(option.value)));

function selectVisible() {
    emit('update:modelValue', [...props.modelValue, ...visibleOptions.value.map((option) => option.value).filter((value) => !isSelected(value))]);
}

function clear() {
    emit('update:modelValue', []);
}
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button
                variant="ghost"
                size="sm"
                class="px-2 gap-x-1.5 font-medium text-gray-500 data-[state=open]:bg-gray-100"
                :class="{ 'text-sky-600': modelValue.length > 0 }"
                :aria-label="title"
                :title="i18n.global.t('global.uiElements.dataTable.filter.texts.filterByColumn').replace('%COLUMN%', title)"
            >
                <ListFilter class="size-4 flex-shrink-0" aria-hidden="true" />
                <Badge v-if="modelValue.length > 0" variant="secondary" class="rounded-md px-1.5 py-0 font-normal">{{ modelValue.length }}</Badge>
            </Button>
        </PopoverTrigger>
        <PopoverContent :align="align" class="p-0 w-max min-w-64 max-w-sm">
            <div class="p-2 space-y-2">
                <div class="font-medium px-1 truncate">{{ title }}</div>
                <Input v-model="search" :placeholder="$t('global.uiElements.dataTable.filter.texts.search')" class="h-9" />
            </div>
            <Separator />
            <ScrollArea class="max-h-64">
                <div class="p-1">
                    <label v-for="option in visibleOptions" :key="option.value" class="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-100">
                        <Checkbox :checked="isSelected(option.value)" @update:checked="toggle(option.value)" />
                        <span class="min-w-0 break-words">{{ option.label }}</span>
                    </label>
                    <div v-if="!visibleOptions.length" class="px-2 py-3 text-center text-gray-500">{{ $t('global.uiElements.dataTable.filter.texts.noOptions') }}</div>
                </div>
            </ScrollArea>
            <Separator />
            <div class="p-1 flex gap-1">
                <Button v-if="selectAll" variant="ghost" size="sm" class="flex-1 justify-center" :disabled="allVisibleSelected" @click="selectVisible">
                    <ListChecks class="size-4 mr-2" aria-hidden="true" />
                    {{ $t('global.uiElements.multiSelect.selectAllButtonLabel') }}
                </Button>
                <Button variant="ghost" size="sm" class="flex-1 justify-center" :disabled="modelValue.length === 0" @click="clear">
                    <RefreshCw class="size-4 mr-2" aria-hidden="true" />
                    {{ $t('global.uiElements.dataTable.filter.buttons.clear') }}
                </Button>
            </div>
            <div v-if="selectedLabels.length" class="sr-only">{{ selectedLabels.join(', ') }}</div>
        </PopoverContent>
    </Popover>
</template>
